import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import {
  buildEnvironmentComparison,
  filterEnvironmentComparison,
  summarizeEnvironmentComparison,
  type EnvironmentComparisonFilter,
  type EnvironmentComparisonRow as ComparisonRow,
} from "./environmentComparison";
import { EnvironmentComparisonRow } from "./EnvironmentComparisonRow";
import type {
  RuntimeFlagsPayload,
  RuntimeVarClassFilter,
} from "./runtimeFlags";

const COMPARISON_FILTERS: Array<{
  id: EnvironmentComparisonFilter;
  label: string;
}> = [
  { id: "attention", label: "Need attention" },
  { id: "all", label: "All variables" },
  { id: "different", label: "Different" },
  { id: "only-stg", label: "Production only" },
  { id: "only-staging", label: "Engineering only" },
  { id: "protected", label: "Protected" },
  { id: "same", label: "Same" },
];

interface EnvironmentComparisonTableProps {
  stg: RuntimeFlagsPayload;
  staging: RuntimeFlagsPayload;
  search: string;
  classFilter: RuntimeVarClassFilter;
}

export function EnvironmentComparisonTable({
  stg,
  staging,
  search,
  classFilter,
}: EnvironmentComparisonTableProps) {
  const [comparisonFilter, setComparisonFilter] =
    useState<EnvironmentComparisonFilter>("attention");
  const rows = useMemo(
    () => buildEnvironmentComparison(stg, staging),
    [stg, staging],
  );
  const summary = useMemo(() => summarizeEnvironmentComparison(rows), [rows]);
  const filteredRows = useMemo(
    () =>
      filterEnvironmentComparison(rows, search, classFilter, comparisonFilter),
    [classFilter, comparisonFilter, rows, search],
  );
  const groups = useMemo(() => groupByService(filteredRows), [filteredRows]);
  const protectedMetadataDiffCount = rows.filter(
    (row) => row.protectedMetadataDiffers,
  ).length;
  const attentionCount =
    summary.different +
    summary["only-stg"] +
    summary["only-staging"] +
    protectedMetadataDiffCount;
  const filterCounts: Record<EnvironmentComparisonFilter, number> = {
    attention: attentionCount,
    all: rows.length,
    different: summary.different,
    "only-stg": summary["only-stg"],
    "only-staging": summary["only-staging"],
    protected: summary.protected,
    same: summary.same,
  };

  return (
    <Card className="w-full gap-4 overflow-hidden">
      <CardHeader className="space-y-4 border-b pb-4">
        <div>
          <CardTitle className="text-base">Environment comparison</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Values are paired by service, container, and variable. Long values
            wrap in place; protected secret values are never compared.
          </p>
        </div>
        <div
          className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7"
          aria-label="Comparison state filter"
        >
          {COMPARISON_FILTERS.map((filter) => {
            const isSelected = comparisonFilter === filter.id;
            return (
              <Button
                key={filter.id}
                variant={isSelected ? "default" : "outline"}
                className={cn(
                  "h-auto min-w-0 justify-between gap-2 px-3 py-2",
                  filter.id === "attention" &&
                    isSelected &&
                    attentionCount > 0 &&
                    "bg-destructive text-white hover:bg-destructive/90",
                )}
                onClick={() => setComparisonFilter(filter.id)}
                aria-pressed={isSelected}
              >
                <span className="truncate text-xs">{filter.label}</span>
                <span className="rounded bg-background/15 px-1.5 font-mono text-xs tabular-nums">
                  {filterCounts[filter.id]}
                </span>
              </Button>
            );
          })}
        </div>
      </CardHeader>
      <CardContent className="min-w-0 space-y-4 px-3 sm:px-6">
        {filteredRows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No variables match the current filters.
          </p>
        ) : (
          <div className="space-y-5" data-comparison-layout="responsive-list">
            {groups.map((group) => (
              <section key={group.serviceId} className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2 px-1">
                  <div className="min-w-0">
                    <h2 className="text-sm font-semibold">
                      {group.serviceLabel}
                    </h2>
                    <p className="break-words font-mono text-[11px] text-muted-foreground [overflow-wrap:anywhere]">
                      {group.serviceId}
                    </p>
                  </div>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {group.rows.length}{" "}
                    {group.rows.length === 1 ? "variable" : "variables"}
                  </span>
                </div>
                <div className="space-y-2">
                  {group.rows.map((row) => (
                    <EnvironmentComparisonRow key={row.key} row={row} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface ComparisonGroup {
  serviceId: string;
  serviceLabel: string;
  rows: ComparisonRow[];
}

function groupByService(rows: ComparisonRow[]): ComparisonGroup[] {
  const groups = new Map<string, ComparisonGroup>();
  for (const row of rows) {
    const group = groups.get(row.serviceId);
    if (group) {
      group.rows.push(row);
      continue;
    }
    groups.set(row.serviceId, {
      serviceId: row.serviceId,
      serviceLabel: row.serviceLabel,
      rows: [row],
    });
  }
  return [...groups.values()];
}
